import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Flame, Zap, BadgePercent, Award, Truck, BadgeCheck, Sparkles, Clock, ArrowRight, Gift, Play, ChevronLeft, ChevronRight } from 'lucide-react';
import ProductCard from './ProductCard';
import CategorySection from './CategorySection';
import HorizontalProductRow from './HorizontalProductRow';
import VideoFeed from './VideoFeed';
import Image from './Image';
import Web3DViewer from './Web3DViewer';
import { isRTL, getTranslation as t } from '../utils/translations';

import { resolveSectionProducts, resolveSectionCategories, resolveSectionVideos } from '../utils/sectionResolver';

// Simple countdown timer hook/component
const CountdownTimer = ({ endTime }) => {
    const [timeLeft, setTimeLeft] = useState('');

    useEffect(() => {
        if (!endTime) return;
        const target = new Date(endTime).getTime();
        if (isNaN(target)) return;

        const updateTimer = () => {
            const now = Date.now();
            const difference = target - now;
            if (difference <= 0) {
                setTimeLeft('00h : 00m : 00s');
                return;
            }
            const hours = Math.floor(difference / (1000 * 60 * 60));
            const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((difference % (1000 * 60)) / 1000);
            
            const pad = (n) => String(n).padStart(2, '0');
            setTimeLeft(`${pad(hours)}h : ${pad(minutes)}m : ${pad(seconds)}s`);
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [endTime]);

    return (
        <div className="countdown-widget glass-panel" style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '12px',
            fontSize: '0.875rem', fontWeight: '600', color: 'var(--text)', border: '1px solid rgba(123, 97, 255, 0.2)'
        }}>
            <Clock size={14} className="text-primary animate-pulse" />
            <span>{timeLeft}</span>
        </div>
    );
};

// High-End Luxury Category Tabs Showcase (Mode B)
const CategoryTabsShowcase = ({ section, categories = [], allProducts = [], onAddToCart, cols = 4 }) => {
    const rtl = isRTL();
    const [activeCatId, setActiveCatId] = useState(null);

    const validCategories = useMemo(() => {
        if (!Array.isArray(categories) || categories.length === 0) return [];
        return categories;
    }, [categories]);

    useEffect(() => {
        if (validCategories.length > 0 && (activeCatId === null || !validCategories.some(c => c.id === activeCatId))) {
            setActiveCatId(validCategories[0].id);
        }
    }, [validCategories, activeCatId]);

    const activeCategory = useMemo(() => {
        return validCategories.find(c => c.id === activeCatId) || validCategories[0] || null;
    }, [validCategories, activeCatId]);

    const activeProducts = useMemo(() => {
        if (!activeCategory) return [];
        const catId = Number(activeCategory.id);
        const catName = String(activeCategory.name || '').toLowerCase();
        const limit = Number(section.productLimit) || 8;

        return allProducts.filter(p => {
            const pCatId = Number(p.categoryId || p.category?.id || 0);
            const pCatName = String(p.categoryName || p.category || '').toLowerCase();
            return (catId && pCatId === catId) || (catName && pCatName === catName);
        }).slice(0, limit);
    }, [allProducts, activeCategory, section.productLimit]);

    if (validCategories.length === 0) return null;

    const activeCatName = activeCategory ? (
        rtl ? (activeCategory.nameAr || activeCategory.name_ar || activeCategory.name)
            : (activeCategory.nameEn || activeCategory.name_en || activeCategory.name)
    ) : '';

    const activeCatSlug = activeCategory?.slug || activeCategory?.name || 'all';

    return (
        <div className="luxury-category-tabs-showcase">
            <div className="luxury-category-tab-bar" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                overflowX: 'auto',
                paddingBottom: '12px',
                marginBottom: '24px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}>
                {validCategories.map(cat => {
                    const isSelected = cat.id === activeCatId;
                    const name = rtl ? (cat.nameAr || cat.name_ar || cat.name) : (cat.nameEn || cat.name_en || cat.name);
                    const count = Number(cat.productCount ?? cat.count ?? 0);
                    return (
                        <button
                            key={cat.id}
                            type="button"
                            onClick={() => setActiveCatId(cat.id)}
                            className={`luxury-tab-pill ${isSelected ? 'active' : ''}`}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '9px 20px',
                                borderRadius: '999px',
                                fontSize: '0.875rem',
                                fontWeight: '700',
                                letterSpacing: '0.04em',
                                border: isSelected ? '1px solid #FFFFFF' : '1px solid rgba(0, 0, 0, 0.1)',
                                background: isSelected ? '#111827' : 'rgba(255, 255, 255, 0.85)',
                                color: isSelected ? '#ffffff' : '#4b5563',
                                cursor: 'pointer',
                                transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                                whiteSpace: 'nowrap',
                                boxShadow: isSelected ? '0 4px 16px rgba(0, 0, 0, 0.2)' : 'none'
                            }}
                        >
                            <span>{name}</span>
                            {count > 0 && (
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '2px 7px',
                                    borderRadius: '10px',
                                    background: isSelected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.06)',
                                    color: isSelected ? '#e5c378' : '#6b7280',
                                    fontWeight: '700'
                                }}>
                                    {count}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {activeProducts.length > 0 ? (
                <div className="products-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    gap: isMobile ? '10px' : (section.gap || '24px')
                }}>
                    {activeProducts.map(product => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            onAddToCart={onAddToCart}
                            cardHoverStyle={section.cardHoverStyle}
                            customBorderColor={section.customBorderColor}
                            luxuryHighlights={section.luxuryHighlights}
                        />
                    ))}
                </div>
            ) : (
                <div style={{
                    textAlign: 'center',
                    padding: '48px 24px',
                    background: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '16px',
                    border: '1px dashed rgba(0, 0, 0, 0.1)',
                    color: '#6b7280'
                }}>
                    <p style={{ margin: 0, fontSize: '0.95rem' }}>
                        {rtl ? 'لا توجد منتجات متوفرة حالياً في هذه المجموعة' : 'No products currently in this collection'}
                    </p>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '28px' }}>
                <Link
                    to={`/category/${encodeURIComponent(activeCatSlug)}`}
                    className="btn-primary"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 28px',
                        fontSize: '0.9rem',
                        letterSpacing: '0.04em'
                    }}
                >
                    <span>{rtl ? `عرض جميع منتجات ${activeCatName}` : `View All in ${activeCatName}`}</span>
                    <ArrowRight size={15} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                </Link>
            </div>
        </div>
    );
};

export const DynamicSectionsRenderer = ({
    page,
    position,
    subSections, // Can render passed structures recursively
    allProducts = [],
    allCategories = [],
    allVideos = [],
    onAddToCart,
    onBuyNow,
    storeSettings
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handleResize = () => {
            const w = window.innerWidth;
            setIsMobile(w < 768);
            setIsTablet(w >= 768 && w < 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // ── Get context info for Gating ──
    const userCountry = useMemo(() => {
        try {
            const geo = JSON.parse(localStorage.getItem('luxvono_geo_context') || '{}');
            return String(geo.country || '').toUpperCase().trim();
        } catch (_) { return ''; }
    }, []);

    const userLang = useMemo(() => {
        try {
            const geo = JSON.parse(localStorage.getItem('luxvono_geo_context') || '{}');
            return String(geo.language || 'en').toLowerCase().trim();
        } catch (_) { return 'en'; }
    }, []);

    const isLoggedIn = useMemo(() => {
        return !!(localStorage.getItem('lux_access_token') || localStorage.getItem('admin_token'));
    }, []);

    // ── Gating filter algorithm ──
    const sectionsToRender = useMemo(() => {
        const rawList = subSections || storeSettings?.homepageSections || [];
        if (!Array.isArray(rawList)) return [];

        return rawList.filter(section => {
            if (!section || section.enabled === false) return false;

            // 1. Page & Position routing
            if (!subSections) {
                // Root elements require matching targetPage and pagePosition
                const targetPage = String(section.targetPage || 'home-page');
                const pagePosition = String(section.pagePosition || 'bottom');
                if (targetPage !== 'all-pages' && targetPage !== page) return false;
                if (pagePosition !== position) return false;
            }

            // 2. Schedule range
            const now = new Date();
            if (section.publishStart) {
                const start = new Date(section.publishStart);
                if (!isNaN(start.getTime()) && now < start) return false;
            }
            if (section.publishEnd) {
                const end = new Date(section.publishEnd);
                if (!isNaN(end.getTime()) && now > end) return false;
            }

            // 3. Device checking
            if (section.mobileOnly && !isMobile) return false;
            if (section.desktopOnly && (isMobile || isTablet)) return false;

            // 4. Country checking
            if (Array.isArray(section.targetCountries) && section.targetCountries.length > 0) {
                const matched = section.targetCountries.some(c => String(c || '').toUpperCase().trim() === userCountry);
                if (!matched) return false;
            }

            // 5. Language checking
            if (Array.isArray(section.targetLanguages) && section.targetLanguages.length > 0) {
                const matched = section.targetLanguages.some(l => String(l || '').toLowerCase().trim() === userLang);
                if (!matched) return false;
            }

            // 6. User verification
            const targetUsers = String(section.targetUsers || 'all').toLowerCase();
            if (targetUsers === 'logged-in' && !isLoggedIn) return false;
            if (targetUsers === 'guests' && isLoggedIn) return false;

            return true;
        });
    }, [subSections, storeSettings?.homepageSections, page, position, isMobile, isTablet, userCountry, userLang, isLoggedIn]);

    if (sectionsToRender.length === 0) return null;

    return (
        <>
            {sectionsToRender.map((section, idx) => {
                const title = t(isRTL()
                    ? (section.titleAr || section.titleEn || section.title)
                    : (section.titleEn || section.titleAr || section.title));
                const desc = t(isRTL()
                    ? (section.descAr || section.descEn || section.description)
                    : (section.descEn || section.descAr || section.description));

                const sectionProducts = resolveSectionProducts(section, allProducts);
                const sectionCategories = resolveSectionCategories(section, allCategories);
                const sectionVideos = resolveSectionVideos(section, allVideos);

                // Styling configuration
                const containerStyle = {
                    paddingTop: section.paddingTop || '0px',
                    paddingBottom: section.paddingBottom || '0px',
                    gap: section.gap || '24px',
                    position: 'relative'
                };

                const backdropStyle = {};
                if (section.customBackground) {
                    backdropStyle.background = section.customBackground;
                }
                if (section.customGradient) {
                    backdropStyle.backgroundImage = section.customGradient;
                }
                if (section.glassEffect) {
                    backdropStyle.background = `rgba(255, 255, 255, ${section.glassOpacity || 0.72})`;
                    backdropStyle.backdropFilter = `blur(${section.blurIntensity || '16px'})`;
                    backdropStyle.WebkitBackdropFilter = `blur(${section.blurIntensity || '16px'})`;
                    backdropStyle.border = '1px solid rgba(255,255,255,0.7)';
                    backdropStyle.boxShadow = '0 8px 32px 0 rgba(31, 38, 135, 0.08)';
                    backdropStyle.borderRadius = 'var(--lux-radius-lg, 24px)';
                    backdropStyle.padding = '24px';
                }

                // Grid layout resolution (Strictly 2 columns on mobile for professional luxury presentation)
                const cols = isMobile 
                    ? 2 
                    : isTablet 
                        ? (section.columnsTablet || 3) 
                        : (section.columnsDesktop || 4);

                const renderBadge = (icon, text) => (
                    <span className="lux-marketplace-badge" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px',
                        borderRadius: '30px', fontSize: '0.75rem', fontWeight: '700',
                        background: 'rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.1)', color: '#111827'
                    }}>
                        {icon}
                        <span>{text}</span>
                    </span>
                );

                const renderUrgencyBanner = () => {
                    const isSponsored = section.type === 'sponsored' || section.urgencyBadgeType === 'sponsored';
                    if (!section.urgencyBadgeEnabled && !isSponsored) return null;
                    const type = isSponsored ? 'sponsored' : (section.urgencyBadgeType || 'hot-deal');
                    const texts = {
                        'hot-deal': { en: 'HOT DEAL', ar: 'عرض ساخن', color: '#FE2C55' },
                        'trending-now': { en: 'TRENDING NOW', ar: 'شائع الآن', color: '#F59E0B' },
                        'most-viewed': { en: 'MOST VIEWED', ar: 'الأكثر مشاهدة', color: '#111111' },
                        'most-sold': { en: 'MOST SOLD', ar: 'الأكثر مبيعاً', color: '#10B981' },
                        'recommended-for-you': { en: 'RECOMMENDED FOR YOU', ar: 'موصى به لك', color: '#111827' },
                        'sponsored': { en: 'SPONSORED', ar: 'ممول', color: '#000000' }
                    };
                    const info = texts[type] || texts['hot-deal'];
                    return (
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            background: info.color, color: type === 'sponsored' ? '#1A1A1A' : '#fff', padding: '6px 12px',
                            borderRadius: '6px', fontWeight: 'bold', fontSize: '0.75rem',
                            letterSpacing: '0.05em', alignSelf: 'flex-start', marginBottom: '8px'
                        }}>
                            <Flame size={12} className="animate-bounce" />
                            <span>{isRTL() ? info.ar : info.en}</span>
                        </div>
                    );
                };

                const renderSectionTitleBlock = () => {
                    if (!title && !desc && !section.timerEnabled && !section.urgencyBadgeEnabled) return null;
                    return (
                        <div className="section-header" style={{
                            marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px'
                        }}>
                            {renderUrgencyBanner()}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                width: '100%', flexWrap: 'wrap', gap: '16px'
                            }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {section.iconClass && (
                                            <span className="section-icon" style={{
                                                fontSize: '1.25rem', color: section.customColor || 'var(--lux-primary)'
                                            }}>
                                                {section.iconClass}
                                            </span>
                                        )}
                                        <h2 className="section-title" style={{
                                            color: section.customColor || 'inherit', margin: 0
                                        }}>
                                            {title}
                                        </h2>
                                        {/* Dynamic badges */}
                                        {section.badgeFire && renderBadge(<Flame size={12} fill="#EF4444" stroke="none" />, t('HOT') || 'Hot')}
                                        {section.badgeDiscount && renderBadge(<BadgePercent size={12} />, section.specialDiscountValue || '-50%')}
                                        {section.badgeLightning && renderBadge(<Zap size={12} fill="#F59E0B" stroke="none" />, t('FLASH') || 'Flash')}
                                        {section.badgeLuxury && renderBadge(<Award size={12} />, t('LUXURY') || 'Luxury')}
                                        {section.badgeFreeShipping && renderBadge(<Truck size={12} />, t('FREE_SHIP') || 'Free Shipping')}
                                        {section.badgeVerified && renderBadge(<BadgeCheck size={12} />, t('VERIFIED') || 'Verified')}
                                        {section.badgeAIRecommended && renderBadge(<Sparkles size={12} />, t('AI_RECOMMENDED') || 'AI Recommended')}
                                    </div>
                                    {desc && <p className="section-subtitle" style={{ margin: '4px 0 0 0' }}>{desc}</p>}
                                </div>

                                {section.timerEnabled && section.timerEndTime && (
                                    <CountdownTimer endTime={section.timerEndTime} />
                                )}
                            </div>
                            <div className="section-accent" style={{ background: section.customColor || 'var(--lux-primary)', marginTop: '4px' }} />
                        </div>
                    );
                };

                const renderProductsContent = () => {
                    if (sectionProducts.length === 0 && allProducts.length === 0) return null;
                    const cardStyle = section.cardLayout || 'vertical';

                    if (cardStyle === 'spotlight-motion' || section.type === 'motion-spotlight' || section.type === 'motion_spotlight') {
                        return renderSpotlightMotionFloor();
                    }

                    if (cardStyle === 'marquee-scroll' || section.type === 'marquee-products' || section.type === 'marquee_products' || (section.motionEnabled && section.motionType === 'infinite-marquee')) {
                        return renderMarqueeProductsRail();
                    }

                    if (cardStyle === 'horizontal') {
                        return (
                            <HorizontalProductRow
                                products={sectionProducts}
                                rowCount={Number(section.rowCount) || 1}
                                onAddToCart={onAddToCart}
                            />
                        );
                    }

                    if (cardStyle === 'carousel') {
                        return (
                            <div className="horizontal-product-row-scroll" style={{ gap: section.gap || '24px' }}>
                                {sectionProducts.map(product => (
                                    <div className="horizontal-product-item" key={product.id} style={{ minWidth: '260px' }}>
                                        <ProductCard 
                                            product={product} 
                                            onAddToCart={onAddToCart} 
                                            cardHoverStyle={section.cardHoverStyle}
                                            customBorderColor={section.customBorderColor}
                                            luxuryHighlights={section.luxuryHighlights}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    if (cardStyle === 'masonry') {
                        // Renders masonry style columns using CSS Columns
                        return (
                            <div style={{
                                columnCount: cols,
                                columnGap: section.gap || '24px',
                                width: '100%'
                            }}>
                                {sectionProducts.map(product => (
                                    <div key={product.id} style={{ breakInside: 'avoid', marginBottom: section.gap || '24px' }}>
                                        <ProductCard 
                                            product={product} 
                                            onAddToCart={onAddToCart} 
                                            cardHoverStyle={section.cardHoverStyle}
                                            customBorderColor={section.customBorderColor}
                                            luxuryHighlights={section.luxuryHighlights}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    }

                    // Default or 'grid' or 'vertical' layout
                    return (
                        <div className="products-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                            gap: isMobile ? '10px' : (section.gap || '24px')
                        }}>
                            {sectionProducts.map(product => (
                                <div key={product.id} className={section.cardTilt3d ? 'tilt-card-3d' : ''} style={{ height: '100%' }}>
                                    <ProductCard 
                                        product={product} 
                                        onAddToCart={onAddToCart} 
                                        cardHoverStyle={section.cardHoverStyle}
                                        customBorderColor={section.customBorderColor}
                                        luxuryHighlights={section.luxuryHighlights}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                };

                const renderCategoryGroupsEditorial = (cats) => {
                    if (!Array.isArray(cats) || cats.length === 0) return null;
                    const rtl = isRTL();

                    const primaryCat = cats[0];
                    const secondaryCats = cats.slice(1, 3);

                    const getImg = (cat, fallbackDefault) => {
                        return cat?.imageUrl || cat?.image_url || cat?.image || fallbackDefault || '';
                    };

                    const getName = (cat) => {
                        return rtl ? (cat?.nameAr || cat?.name_ar || cat?.name || '') : (cat?.nameEn || cat?.name_en || cat?.name || '');
                    };

                    const getSlug = (cat) => cat?.slug || cat?.name || 'all';

                    const fallbackPrimaryImg = section.heroImageUrl || section.imageUrl || 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?auto=format&fit=crop&w=1200&q=80';
                    const fallbackSecondary1 = 'https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=800&q=80';
                    const fallbackSecondary2 = 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?auto=format&fit=crop&w=800&q=80';

                    return (
                        <div className="luxury-editorial-category-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1.35fr 1fr',
                            gap: section.gap || '20px',
                            width: '100%'
                        }}>
                            {/* Focal / Primary Editorial Card */}
                            {primaryCat && (
                                <Link
                                    to={`/category/${encodeURIComponent(getSlug(primaryCat))}`}
                                    className="luxury-editorial-focal-card"
                                    style={{
                                        position: 'relative',
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                        minHeight: isMobile ? '360px' : '480px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-end',
                                        padding: '32px 28px',
                                        textDecoration: 'none',
                                        color: '#ffffff',
                                        border: '1px solid rgba(255, 255, 255, 0.25)',
                                        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.12)'
                                    }}
                                >
                                    <Image
                                        src={getImg(primaryCat, fallbackPrimaryImg)}
                                        alt={getName(primaryCat)}
                                        className="editorial-focal-bg"
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            zIndex: 1,
                                            transition: 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)'
                                        }}
                                    />
                                    {/* Dark luxury vignette overlay */}
                                    <div style={{
                                        position: 'absolute',
                                        inset: 0,
                                        background: 'linear-gradient(to top, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.35) 50%, rgba(0, 0, 0, 0.08) 100%)',
                                        zIndex: 2
                                    }} />

                                    {/* Content */}
                                    <div style={{ position: 'relative', zIndex: 3 }}>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            padding: '4px 12px',
                                            borderRadius: '30px',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            backdropFilter: 'blur(8px)',
                                            WebkitBackdropFilter: 'blur(8px)',
                                            border: '1px solid rgba(255, 255, 255, 0.4)',
                                            color: '#e5c378',
                                            fontSize: '0.75rem',
                                            fontWeight: '700',
                                            letterSpacing: '0.12em',
                                            textTransform: 'uppercase',
                                            marginBottom: '12px'
                                        }}>
                                            <Sparkles size={12} />
                                            <span>{section.collectionBadge || (rtl ? 'المجموعة الرئيسية' : 'FLAGSHIP COLLECTION')}</span>
                                        </div>

                                        <h3 style={{
                                            fontFamily: 'var(--font-heading, "Playfair Display", Georgia, serif)',
                                            fontSize: isMobile ? '1.8rem' : '2.4rem',
                                            fontWeight: '700',
                                            margin: '0 0 8px 0',
                                            color: '#ffffff',
                                            lineHeight: 1.15
                                        }}>
                                            {getName(primaryCat)}
                                        </h3>

                                        {primaryCat.description && (
                                            <p style={{
                                                fontSize: '0.9rem',
                                                color: 'rgba(255, 255, 255, 0.8)',
                                                margin: '0 0 16px 0',
                                                maxWidth: '420px',
                                                lineHeight: 1.5
                                            }}>
                                                {primaryCat.description}
                                            </p>
                                        )}

                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            color: '#FFFFFF',
                                            fontWeight: '700',
                                            fontSize: '0.875rem',
                                            marginTop: '8px'
                                        }}>
                                            <span>{rtl ? 'اكتشف المجموعة' : 'Explore Collection'}</span>
                                            <ArrowRight size={16} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                                        </div>
                                    </div>
                                </Link>
                            )}

                            {/* Secondary Stacked Landscape Cards */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: section.gap || '20px',
                                height: '100%'
                            }}>
                                {secondaryCats.map((cat, sIdx) => {
                                    const fallbackImg = sIdx === 0 ? fallbackSecondary1 : fallbackSecondary2;
                                    return (
                                        <Link
                                            key={cat.id || sIdx}
                                            to={`/category/${encodeURIComponent(getSlug(cat))}`}
                                            className="luxury-editorial-secondary-card"
                                            style={{
                                                position: 'relative',
                                                borderRadius: '18px',
                                                overflow: 'hidden',
                                                flex: 1,
                                                minHeight: isMobile ? '200px' : '230px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'flex-end',
                                                padding: '24px 20px',
                                                textDecoration: 'none',
                                                color: '#ffffff',
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
                                            }}
                                        >
                                            <Image
                                                src={getImg(cat, fallbackImg)}
                                                alt={getName(cat)}
                                                className="editorial-secondary-bg"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    zIndex: 1,
                                                    transition: 'transform 0.65s cubic-bezier(0.16, 1, 0.3, 1)'
                                                }}
                                            />
                                            <div style={{
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'linear-gradient(to top, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.25) 60%, transparent 100%)',
                                                zIndex: 2
                                            }} />

                                            <div style={{ position: 'relative', zIndex: 3 }}>
                                                <span style={{
                                                    fontSize: '0.7rem',
                                                    fontWeight: '700',
                                                    letterSpacing: '0.1em',
                                                    textTransform: 'uppercase',
                                                    color: '#FFFFFF',
                                                    marginBottom: '4px',
                                                    display: 'block'
                                                }}>
                                                    {rtl ? 'تشكيلة حصرية' : 'CURATED'}
                                                </span>
                                                <h4 style={{
                                                    fontSize: '1.35rem',
                                                    fontWeight: '700',
                                                    margin: '0 0 4px 0',
                                                    color: '#ffffff'
                                                }}>
                                                    {getName(cat)}
                                                </h4>
                                                <div style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    fontSize: '0.8rem',
                                                    color: 'rgba(255, 255, 255, 0.8)',
                                                    fontWeight: '600'
                                                }}>
                                                    <span>{rtl ? 'تسوق الآن' : 'Shop Now'}</span>
                                                    <ArrowRight size={13} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    );
                };

                const render3DShowcase = (sec) => {
                    const rtl = isRTL();
                    const modelTitle = rtl 
                        ? (sec.titleAr || sec.title || 'ساعة يد إمبراطورية فاخرة (Three.js 3D)') 
                        : (sec.titleEn || sec.title || 'Imperial Haute Horlogerie 3D');
                    const modelDesc = rtl 
                        ? (sec.descAr || sec.descEn || 'استكشف أدق التفاصيل الفاخرة مع محرك التدوير التفاعلي 360° ثلاثي الأبعاد وإضاءة الاستوديو الديناميكية.') 
                        : (sec.descEn || sec.descAr || 'Experience artisanal precision in full 360° interactive 3D with dynamic studio lighting and AR projection.');
                    const tag = sec.showcaseTag || (rtl ? '✦ تجربة مكانية ثلاثية الأبعاد 3D' : '✦ 3D SPATIAL COMMERCE');
                    const actionText = sec.showcaseActionText || (rtl ? 'استكشف التشكيلة الكاملة' : 'Explore 3D Collection');
                    const actionUrl = sec.showcaseActionUrl || '/category/1';

                    return (
                        <div className="luxury-3d-showcase-container">
                            <div className="luxury-3d-canvas-wrapper">
                                <Web3DViewer
                                    glbUrl={sec.glbUrl || ''}
                                    usdzUrl={sec.usdzUrl || ''}
                                    productName={modelTitle}
                                    lightingPreset={sec.lightingPreset || 'studio'}
                                />
                            </div>
                            <div className="luxury-3d-content-card">
                                <div className="luxury-3d-badge">
                                    <Sparkles size={14} style={{ color: '#FFFFFF' }} />
                                    <span>{tag}</span>
                                </div>
                                <h3 className="luxury-3d-title">{modelTitle}</h3>
                                <p className="luxury-3d-desc">{modelDesc}</p>
                                <div className="luxury-3d-features-list">
                                    <div className="luxury-3d-feature-chip">
                                        <span>🔄</span>
                                        <span>{rtl ? 'تدوير حر 360° بالماوس واللمس' : '360° Real-time Orbit'}</span>
                                    </div>
                                    <div className="luxury-3d-feature-chip">
                                        <span>💡</span>
                                        <span>{rtl ? 'إضاءة استوديو واقعية فائقة' : 'Dynamic Studio Lighting'}</span>
                                    </div>
                                    <div className="luxury-3d-feature-chip">
                                        <span>📱</span>
                                        <span>{rtl ? 'واقع معزز (AR) للأجهزة الذكية' : 'Augmented Reality (AR)'}</span>
                                    </div>
                                </div>
                                {actionUrl && (
                                    <Link to={actionUrl} className="luxury-3d-cta-btn">
                                        <span>{actionText}</span>
                                        <ArrowRight size={15} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                                    </Link>
                                )}
                            </div>
                        </div>
                    );
                };

                const renderSpotlightMotionFloor = () => {
                    const rawList = sectionProducts.length > 0 ? sectionProducts : allProducts;
                    const displayProducts = (rawList || []).slice(0, 4);
                    const rtl = isRTL();
                    const mediaSide = section.spotlightMediaSide || 'left';
                    const videoSrc = section.spotlightVideoUrl || section.videoUrl || '';
                    const imageSrc = section.imageUrl || section.bannerImage || 'https://images.unsplash.com/photo-1512436991641-6745cdb1723f?auto=format&fit=crop&w=1200&q=80';
                    const badgeText = section.spotlightBadgeText || (rtl ? '✦ طابق العرض التفاعلي' : '✦ LIVE MOTION FLOOR');
                    const titleText = rtl ? (section.titleAr || section.titleEn || section.title) : (section.titleEn || section.title || section.titleAr);
                    const actionUrl = section.spotlightActionUrl || section.bannerLink || '/category/all';
                    const actionText = section.spotlightActionText || (rtl ? 'اكتشف التشكيلة ➔' : 'Explore Collection ➔');

                    return (
                        <div className={`luxury-motion-floor-container ${mediaSide === 'right' ? 'media-right' : 'media-left'}`}>
                            {/* 1. The Large Spotlight Video / Motion Card */}
                            <div className="luxury-spotlight-motion-card">
                                {videoSrc ? (
                                    <video
                                        className="spotlight-bg-video"
                                        src={videoSrc}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                    />
                                ) : (
                                    <div 
                                        className="spotlight-bg-image"
                                        style={{ backgroundImage: `url(${imageSrc})` }}
                                    />
                                )}
                                <div className="spotlight-overlay" />
                                
                                <div className="spotlight-card-content">
                                    <div className="spotlight-live-badge">
                                        <span className="live-dot" />
                                        <span>{badgeText}</span>
                                    </div>

                                    <div className="spotlight-play-indicator">
                                        <div className="play-pulse-circle">
                                            <Play size={22} fill="#ffffff" color="#ffffff" style={{ marginLeft: rtl ? 0 : '3px' }} />
                                        </div>
                                    </div>

                                    <div className="spotlight-info">
                                        <h3 className="spotlight-headline">{titleText}</h3>
                                        {(section.descAr || section.descEn) && (
                                            <p className="spotlight-subtext">
                                                {rtl ? (section.descAr || section.descEn) : (section.descEn || section.descAr)}
                                            </p>
                                        )}
                                        {actionUrl && (
                                            <Link to={actionUrl} className="spotlight-action-cta">
                                                <span>{actionText}</span>
                                                <ArrowRight size={14} style={{ transform: rtl ? 'rotate(180deg)' : 'none' }} />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* 2. The 2x2 Product Tiles Grid */}
                            <div className="luxury-spotlight-products-grid">
                                {displayProducts.map((prod, pIdx) => (
                                    <div key={prod.id || pIdx} className={`spotlight-product-cell ${section.cardTilt3d ? 'tilt-card-3d' : ''}`}>
                                        <ProductCard
                                            product={prod}
                                            onAddToCart={onAddToCart}
                                            cardHoverStyle={section.cardHoverStyle}
                                            customBorderColor={section.customBorderColor}
                                            luxuryHighlights={section.luxuryHighlights}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                };

                const renderMarqueeProductsRail = () => {
                    const rawProducts = sectionProducts.length > 0 ? sectionProducts : allProducts;
                    if (!rawProducts || rawProducts.length === 0) return null;
                    const displayList = rawProducts.slice(0, 12);
                    const rtl = isRTL();
                    const speedSec = section.motionSpeed === 'slow' ? 38 : section.motionSpeed === 'fast' ? 16 : 24;
                    const marqueeItems = [...displayList, ...displayList];

                    return (
                        <div className="luxury-marquee-section-wrapper">
                            <div className="marquee-toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="marquee-live-chip">
                                        <Zap size={13} style={{ color: '#FE2C55' }} />
                                        <span>{rtl ? 'شريط عروض متحرك تلقائياً' : 'LIVE AUTO-SLIDING RAIL'}</span>
                                    </span>
                                </div>
                            </div>

                            <div className="infinite-marquee-viewport">
                                <div className="marquee-fade-edge left" />
                                <div className="marquee-fade-edge right" />

                                <div 
                                    className="infinite-marquee-track"
                                    style={{
                                        display: 'flex',
                                        gap: section.gap || '20px',
                                        width: 'max-content',
                                        animation: `luxuryMarquee ${speedSec}s linear infinite`,
                                        animationDirection: rtl ? 'reverse' : 'normal'
                                    }}
                                >
                                    {marqueeItems.map((prod, idx) => (
                                        <div 
                                            key={`${prod.id || idx}_${idx}`} 
                                            className={`marquee-product-tile ${section.cardTilt3d ? 'tilt-card-3d' : ''}`}
                                            style={{
                                                flex: '0 0 230px',
                                                width: '230px',
                                                minWidth: '230px'
                                            }}
                                        >
                                            <ProductCard
                                                product={prod}
                                                onAddToCart={onAddToCart}
                                                cardHoverStyle={section.cardHoverStyle}
                                                customBorderColor={section.customBorderColor}
                                                luxuryHighlights={section.luxuryHighlights}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                };

                const renderContentInner = () => {
                    switch (section.type) {
                        case '3d-showcase':
                        case 'three_d_showcase':
                        case '3d_spatial':
                            return render3DShowcase(section);

                        case 'motion-spotlight':
                        case 'motion_spotlight':
                            return renderSpotlightMotionFloor();

                        case 'marquee-products':
                        case 'marquee_products':
                            return renderMarqueeProductsRail();

                        case 'category-groups':
                        case 'category_groups':
                            if (sectionCategories.length === 0) return null;
                            if (section.categoryGroupStyle === 'tabs') {
                                return (
                                    <CategoryTabsShowcase
                                        section={section}
                                        categories={sectionCategories}
                                        allProducts={allProducts}
                                        onAddToCart={onAddToCart}
                                        cols={cols}
                                    />
                                );
                            }
                            return renderCategoryGroupsEditorial(sectionCategories);

                        case 'categories':
                        case 'categories-slider':
                            if (sectionCategories.length === 0) return null;
                            return (
                                <CategorySection
                                    categories={sectionCategories}
                                    highlightedNames={storeSettings?.categoryHighlights || []}
                                />
                            );

                        case 'videos':
                        case 'video-commerce':
                        case 'video_strip':
                            if (page === 'home-page') return null;
                            const displayVideos = (sectionVideos && sectionVideos.length > 0) ? sectionVideos : allVideos;
                            if (!displayVideos || displayVideos.length === 0) return null;
                            return (
                                <VideoFeed
                                    videos={displayVideos}
                                    title={title || 'VIDEO COMMERCE'}
                                    subtitle={desc || 'Discover through videos'}
                                    onAddToCart={onAddToCart}
                                    onBuyNow={onBuyNow}
                                    showViewAll
                                />
                            );

                        case 'banner-section':
                        case 'custom-banner':
                            const linkUrl = section.bannerLink || '/category/all';
                            const isLinkExternal = String(linkUrl).startsWith('http');
                            const hasVid = !!section.videoUrl;

                            return (
                                <div style={{
                                    position: 'relative', overflow: 'hidden', minHeight: '320px',
                                    borderRadius: 'var(--lux-radius-lg, 24px)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {hasVid ? (
                                        <video
                                            src={section.videoUrl}
                                            autoPlay muted loop playsInline
                                            style={{
                                                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                objectFit: 'cover', zIndex: 1, pointerEvents: 'none'
                                            }}
                                        />
                                    ) : (
                                        section.imageUrl && (
                                            <img
                                                src={section.imageUrl}
                                                alt={title || 'Banner'}
                                                loading="lazy"
                                                style={{
                                                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                                    objectFit: 'cover', zIndex: 1
                                                }}
                                            />
                                        )
                                    )}
                                    <div style={{
                                        position: 'absolute', inset: 0,
                                        background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 100%)',
                                        zIndex: 2
                                    }} />
                                    <div style={{
                                        position: 'relative', zIndex: 3, textAlign: 'center', color: '#fff',
                                        padding: '36px 24px', maxWidth: '720px'
                                    }}>
                                        {title && <h3 style={{ fontSize: '2.2rem', fontWeight: '700', marginBottom: '12px' }}>{title}</h3>}
                                        {desc && <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '24px' }}>{desc}</p>}
                                        {isLinkExternal ? (
                                            <a href={linkUrl} target="_blank" rel="noreferrer" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                {t('EXPLORE') || 'Explore'}
                                            </a>
                                        ) : (
                                            <Link to={linkUrl} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                {t('EXPLORE') || 'Explore'}
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            );

                        case 'promo-cards':
                            return (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                    gap: section.gap || '24px',
                                    width: '100%'
                                }}>
                                    <div className="home-promo-card home-promo-card--primary" style={{ borderColor: section.customColor || 'transparent' }}>
                                        <div className="home-promo-icon"><Flame size={24} /></div>
                                        <h3>{title || t('HOME_TRENDING_TITLE') || 'Trending Now'}</h3>
                                        <p>{desc || 'Discover the hottest products everyone is talking about'}</p>
                                        <Link to="/category/all" className="home-promo-card-link" style={{ textDecoration: 'none', color: 'var(--lux-primary)', fontWeight: 'bold' }}>{t('SHOP_NOW') || 'Shop Now'} →</Link>
                                    </div>
                                    <div className="home-promo-card home-promo-card--accent">
                                        <div className="home-promo-icon"><Zap size={24} /></div>
                                        <h3>{t('HOME_FLASH_TITLE') || 'Flash Deals'}</h3>
                                        <p>{t('HOME_FLASH_DESC') || 'Limited-time offers with up to 70% off select items'}</p>
                                        <Link to="/category/all" className="home-promo-card-link" style={{ textDecoration: 'none', color: 'var(--lux-primary)', fontWeight: 'bold' }}>{t('SHOP_NOW') || 'Shop Now'} →</Link>
                                    </div>
                                    <div className="home-promo-card home-promo-card--dark">
                                        <div className="home-promo-icon"><Gift size={24} /></div>
                                        <h3>{t('HOME_FREE_SHIP_TITLE') || 'Free Shipping'}</h3>
                                        <p>{t('HOME_FREE_SHIP_DESC') || 'On orders over $50 — worldwide delivery'}</p>
                                        <Link to="/category/all" className="home-promo-card-link" style={{ textDecoration: 'none', color: 'var(--lux-primary)', fontWeight: 'bold' }}>{t('EXPLORE') || 'Explore'} →</Link>
                                    </div>
                                </div>
                            );

                        case 'luxury-brands':
                            const defaultBrands = [
                                { name: 'Rolex', desc: 'Geneva Horology' },
                                { name: 'Chanel', desc: 'Haute Couture' },
                                { name: 'Gucci', desc: 'Florentine Leather' },
                                { name: 'Prada', desc: 'Milanese Elegance' },
                                { name: 'Cartier', desc: 'Fine Jewelry' },
                                { name: 'Hermès', desc: 'Parisian Heritage' }
                            ];
                            return (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                    gap: '20px',
                                    padding: '10px 0',
                                    width: '100%'
                                }}>
                                    {defaultBrands.map((brand) => (
                                        <div key={brand.name} className="brand-card glass-panel" style={{
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                            padding: '24px', borderRadius: 'var(--lux-radius-md, 16px)', textAlign: 'center',
                                            transition: 'all 0.3s ease', cursor: 'pointer', height: '140px', position: 'relative'
                                        }}>
                                            <div style={{
                                                fontFamily: 'var(--lux-font-family, Outfit)', fontSize: '1.5rem', fontWeight: '700',
                                                letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px', color: 'var(--text)'
                                            }}>
                                                {brand.name}
                                            </div>
                                            <div style={{
                                                fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.05em'
                                            }}>
                                                {brand.desc}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            );

                        case 'nested-container':
                            return null;

                        default:
                            return renderProductsContent();
                    }
                };

                return (
                    <React.Fragment key={section.id || `renderer-${idx}`}>
                        <div className="section-separator container" />
                        <section className="section container" style={containerStyle}>
                            <div style={backdropStyle}>
                                {renderSectionTitleBlock()}
                                {renderContentInner()}

                                {/* RECURSIVE RENDER: Infinite Subsection Nesting */}
                                {Array.isArray(section.subSections) && section.subSections.length > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: section.gap || '24px',
                                        marginTop: section.gap || '24px'
                                    }}>
                                        <DynamicSectionsRenderer
                                            page={page}
                                            position={position}
                                            subSections={section.subSections}
                                            allProducts={allProducts}
                                            allCategories={allCategories}
                                            allVideos={allVideos}
                                            onAddToCart={onAddToCart}
                                            onBuyNow={onBuyNow}
                                            storeSettings={storeSettings}
                                        />
                                    </div>
                                )}
                            </div>
                        </section>
                    </React.Fragment>
                );
            })}
        </>
    );
};

export default DynamicSectionsRenderer;
